import { CookieConsentService } from './userConsentService.ts';
import { PostHogService } from './posthogService.ts';

export class ThirdPartyScriptManager {
    private static sentryInitialized = false;
    private static sentryClient: any = null;

    static initializeSentry(sentryConfig: { dsn: string; environment: string }): void {
        if (this.sentryInitialized) 
            return;

        if (!CookieConsentService.hasConsent('analytics')) {
            console.log('Sentry not initialized: analytics cookies not consented');
            return;
        }

        // Dynamic import to avoid loading Sentry if not needed
        import('@sentry/react')
            .then(Sentry => {
                Sentry.init({
                    dsn: sentryConfig.dsn,
                    environment: sentryConfig.environment,
                    sendDefaultPii: true,
                });
                this.sentryClient = Sentry;
                this.sentryInitialized = true;
                console.log('Sentry initialized');
            })
            .catch(error => {
                console.error('Failed to initialize Sentry:', error);
            });
    }

    static disableSentry(): void {
        if (!this.sentryInitialized) 
            return;

        try {
            if (this.sentryClient) {
                // Close the current Sentry client and flush any pending events (v8+ API)
                const client = this.sentryClient.getClient();
                if (client) {
                    client.close(2000).then(() => {
                        console.log('Sentry client closed successfully');
                    }).catch((error: any) => {
                        console.error('Error closing Sentry client:', error);
                    });
                }

                // Disable Sentry by setting client to undefined (v8+ API)
                this.sentryClient.setCurrentClient(undefined);
                console.log('Sentry disabled');
            }
            
            this.sentryInitialized = false;
            this.sentryClient = null;
        } catch (error) {
            console.error('Failed to disable Sentry:', error);
            // Force reset state even if disable fails
            this.sentryInitialized = false;
            this.sentryClient = null;
        }
    }

    static loadAnalyticsScripts(): void {
        // Always initialize PostHog in memory-only mode (no cookies set)
        PostHogService.initialize();

        if (CookieConsentService.hasConsent('analytics')) {
            // Initialize Sentry if not in dev mode
            console.log('Analytics consent granted - loading analytics scripts');
            if (import.meta.env.MODE !== 'dev') {
                this.initializeSentry({
                    dsn: import.meta.env.VITE_SENTRY_DSN,
                    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
                });
            }
            PostHogService.optIn();
        } else {
            // Analytics consent not granted - ensure analytics scripts are disabled
            console.log('Analytics consent not granted - analytics scripts disabled');
            this.disableSentry();
            PostHogService.disable();
        }
    }

    static reloadScripts(): void {
        // Reload scripts based on current consent
        // This will enable scripts if consent is given, or disable them if revoked
        console.log('Reloading third-party scripts based on current consent');
        this.loadAnalyticsScripts();
    }
}
